import { exchangeRateService } from '@/services/exchangeRateService';

/**
 * Utilidades para conversión de monedas y formateo
 */

// Tasa de cambio por defecto como fallback
const DEFAULT_USD_TO_CLP_RATE = 950;

/**
 * Obtiene la tasa de cambio actual USD a CLP (sincrónica con fallback)
 * Para uso en componentes que requieren valor inmediato
 */
export const getUsdToClpRate = () => {
  // Intentar obtener desde cache local
  const cached = localStorage.getItem('exchange_rate_cache');
  if (cached && !isNaN(parseFloat(cached))) {
    return parseFloat(cached);
  }
  
  // Fallback a tasa por defecto
  return DEFAULT_USD_TO_CLP_RATE;
};

/**
 * Obtiene la tasa de cambio actual (asíncrona, desde API)
 * Para uso cuando se necesita la tasa más actualizada
 */
export const getExchangeRateAsync = async () => {
  return await exchangeRateService.getExchangeRate();
};

/**
 * Convierte USD a CLP (sincrónico)
 */
export const convertUsdToClp = (usdAmount) => {
  const rate = getUsdToClpRate();
  return Math.round(usdAmount * rate);
};

/**
 * Convierte USD a CLP (asíncrono, con tasa actualizada)
 */
export const convertUsdToClpAsync = async (usdAmount) => {
  return await exchangeRateService.convertUsdToClp(usdAmount);
};

/**
 * Formatea precio en CLP con separadores de miles
 */
export const formatClp = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Formatea precio en USD con etiqueta clara
 */
export const formatUsd = (amount, includeLabel = true) => {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  
  return includeLabel ? `${formatted} USD` : formatted;
};

/**
 * Mapeo directo de precios de Clerk (en USD) por plan
 */
const CLERK_PLAN_PRICES = {
  'Bronze': { monthly: 45, annual: 432 }, // $45/mes, $432/año
  'Silver': { monthly: 85, annual: 816 }, // $85/mes, $816/año  
  'Gold': { monthly: 180, annual: 1728 }  // $180/mes, $1728/año
};

/**
 * Obtiene precios usando datos reales de Clerk
 */
export const getPlanPricing = (plan, period = 'mensual') => {
  // Obtener precios de Clerk en USD
  const clerkPrices = CLERK_PLAN_PRICES[plan.name] || CLERK_PLAN_PRICES['Bronze'];
  
  const usdMonthly = clerkPrices.monthly;
  const usdAnnual = clerkPrices.annual;
  const usdCurrent = period === 'anual' ? usdAnnual : usdMonthly;
  
  // Convertir a CLP
  const clpMonthly = convertUsdToClp(usdMonthly);
  const clpAnnual = convertUsdToClp(usdAnnual);
  const clpCurrent = period === 'anual' ? clpAnnual : clpMonthly;
  
  // Calcular ahorros (diferencia entre 12 meses vs anual)
  const usdSavings = period === 'anual' ? (usdMonthly * 12) - usdAnnual : 0;
  const clpSavings = period === 'anual' ? (clpMonthly * 12) - clpAnnual : 0;

  return {
    // Precios en CLP (convertidos desde USD de Clerk)
    clp: {
      monthly: clpMonthly,
      annual: clpAnnual,
      current: clpCurrent
    },
    // Precios originales de Clerk en USD
    usd: {
      monthly: usdMonthly,
      annual: usdAnnual,
      current: usdCurrent
    },
    // Ahorros reales
    savings: {
      clp: clpSavings,
      usd: usdSavings
    }
  };
};

/**
 * Actualiza la tasa de cambio (para administradores)
 */
export const updateExchangeRate = (newRate) => {
  if (newRate && !isNaN(parseFloat(newRate)) && parseFloat(newRate) > 0) {
    localStorage.setItem('usd_to_clp_rate', newRate.toString());
    return true;
  }
  return false;
};

/**
 * Mapea precios de Clerk (USD) a formato de display
 */
export const formatClerkPrice = (usdPrice, showBothCurrencies = true) => {
  const clpPrice = convertUsdToClp(usdPrice);
  
  if (showBothCurrencies) {
    return {
      primary: formatClp(clpPrice),
      secondary: `(${formatUsd(usdPrice, true)})`,
      clp: clpPrice,
      usd: usdPrice
    };
  }
  
  return {
    primary: formatClp(clpPrice),
    secondary: null,
    clp: clpPrice,
    usd: usdPrice
  };
};